import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { allowModalBackgroundScroll, stringEqualsIgnoreCase, sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import { consentCookieNames, sandhillsCookieClassifications } from '@Components/Utilities/SandhillsConstants';
import { getAllSavedCookieNames, getConsentCookie, getConsentCookieJSON, getConsentCookieName, getExpireTimeUTCStringForCookies, setConsentChanged, SetSandhillsConsentCookie, showUserCentricsBanner, getRootDomain } from '@Components/Utilities/CookieHelper';
import Modal from '@Components/Shared/Modals/Modal';
import Toggle from '@Components/Shared/Controls/Toggle';
import atoms from '@Components/Utilities/SandhillsAtoms';

const _saveSelections = 'saveselections';

const CookiesConsentView = (props) => {
    const {
        Actions,
        Buttons,
        Toggles,
        Message,
        RequireOptInConsent,
        CookieSettings,
        Classifications,
    } = props;

    const currentUser = useRecoilValue(atoms.currentUser);
    const [ckySettings, setConsentSettings] = useState(CookieSettings);
    const [showModal, setShowModal] = useRecoilState(atoms.showCookieConsent);
    const routes = useRecoilValue(atoms.routes);
    const useUserCentricsCMP = sandhills.TradeSites.React.ComponentProps['useUserCentricsCMP'];

    useEffect(() => {
        if (showModal) {
            allowModalBackgroundScroll();

            //Using footer link to open modal.
            //Check if cookie exists, otherwise, reset ckySettings state.   
            if (RequireOptInConsent) {
                var consentCookie = getConsentCookieJSON();
                if (consentCookie == null) {
                    resetConsentSettings();
                }
            }
        }
    }, [showModal]);

    //on page load, check if user is authenticated and cookie exists
    //if TrackingUserID is not set or different, update cookie with CentralTrackingUserID and save consent preferences to DB
    useEffect(() => {
        setCookiePolicyLinkEvent();
        if (!useUserCentricsCMP) {
            checkAgreementVersion();
            if (RequireOptInConsent) {
                initSettingsAndCookies();
            }
        }
    }, []);

    const initSettingsAndCookies = () => {
        //If the only cookie that exists is a guest cookie, use the preferences for that cookie and create a new sandhills-consent-[UserID] cookie tied to that logged-in user.
        //If both a guest cookie and a UserID cookie exist, and the person logging in matches that UserID, then apply those preferences and do not prompt the user.
        //If both a guest cookie and a UserID cookie exist, and the person logging in does not match the existing UserID cookie, prompt the user with the cookie modal.
        if (currentUser.IsAuthenticated) {
            var guestCookie = getConsentCookie(true);
            var usersCookieNames = getAllSavedCookieNames().filter(c => c.includes(consentCookieNames.SandhillsConsent + '-'));
            var authenticatedUserConsent = getConsentCookie()

            if (guestCookie) {
                if ((usersCookieNames.length == 0 || (guestCookie.ShouldResync || authenticatedUserConsent == null)) && (!guestCookie.TrackingUserID || guestCookie?.TrackingUserID == ckySettings.TrackingUserID)) {
                    setConsentChanged(guestCookie);
                    authenticatedUserConsent = guestCookie;
                    if (guestCookie.TrackingUserID) guestCookie.TrackingUserID = ckySettings.TrackingUserID;
                    SetSandhillsConsentCookie(guestCookie, daysToSetCookie(guestCookie.ConsentStatus), guestCookie.TrackingUserID);
                    setConsentSettings(guestCookie);

                    if (RequireOptInConsent && (guestCookie.ShouldResync || guestCookie?.WebAgreementConsentID <= 0)) {
                        //Don't save unless resync and not tied to WebAgreementConsentID, otherwise we'll create dupes.  
                        saveConsentToDB(guestCookie);
                    }
                }
                else if (RequireOptInConsent && authenticatedUserConsent?.ShouldResync) {
                    saveConsentToDB(authenticatedUserConsent);
                } else if (authenticatedUserConsent && guestCookie.TrackingUserID != authenticatedUserConsent.TrackingUserID) {
                    //sync guest cookie with authenticated user cookie to persist settings after logging out.  
                    SetSandhillsConsentCookie(authenticatedUserConsent, daysToSetCookie(authenticatedUserConsent.ConsentStatus));
                }
            } else if (authenticatedUserConsent) {
                //guest cookie isn't set, create it.   
                SetSandhillsConsentCookie(authenticatedUserConsent, daysToSetCookie(authenticatedUserConsent.ConsentStatus));
            }

            if (authenticatedUserConsent == null) {
                setConsentChanged(ckySettings);
                setShowModal(true);
            }
        }
    }

    const setCookiePolicyLinkEvent = () => {
        const links = document.querySelectorAll('.open-consent-modal, #open-consent-modal');

        links.forEach(link => {
            link.setAttribute('tabIndex', 0);
            link.setAttribute('role', 'button');

            link.onclick = function (e) {
                e.preventDefault();

                if (useUserCentricsCMP) {
                    showUserCentricsBanner(1);
                } else {
                    setShowModal(true);
                }
            };
        });
    };

    const checkAgreementVersion = () => {
        var consentCookie = getConsentCookie();
        var shouldReset = true;
        if (ckySettings?.TrackingUserID > 0 && currentUser.IsAuthenticated) {
            var guestCookie = getConsentCookie(true);
            if (guestCookie?.TrackingUserID == consentCookie?.TrackingUserID &&
                guestCookie?.WebAgreementID == ckySettings?.WebAgreementID &&
                consentCookie?.WebAgreementID != ckySettings?.WebAgreementID) {
                SetSandhillsConsentCookie(guestCookie, daysToSetCookie(guestCookie.ConsentStatus), guestCookie.TrackingUserID);
                shouldReset = false;
            }
        }

        if (consentCookie && shouldReset && consentCookie.WebAgreementID != ckySettings.WebAgreementID) {
            document.cookie = getConsentCookieName() + '=; ' + 'expires=' + getExpireTimeUTCStringForCookies(-1) + '; path=/; domain=.' + getRootDomain();
            resetConsentSettings();
            setShowModal(true);
        }
    }

    const onSubmit = (classification, isAccepting) => {
        var consentCookie = getConsentCookieJSON();

        //Don't run if they're just closing the modal and there's existing consent.   
        if (!consentCookie || (consentCookie && isAccepting)) {
            isAccepting = !RequireOptInConsent || isAccepting;

            setConsentSettings(prvCkySettings => {
                //update ckySettings properties then save to cookies
                const newSettings = {
                    ...prvCkySettings,
                    ConsentStatus: getStatus(isAccepting, prvCkySettings.ConsentStatus),
                    ActionDateTime: new Date().toISOString(),
                    ShouldResync: RequireOptInConsent 
                };

                if (RequireOptInConsent && !stringEqualsIgnoreCase(classification, _saveSelections)) {
                    Classifications.forEach(c => {
                        if (!(stringEqualsIgnoreCase(classification, c) || stringEqualsIgnoreCase(c, sandhillsCookieClassifications.StrictlyNecessary))) {
                            newSettings[c] = !stringEqualsIgnoreCase(classification, sandhillsCookieClassifications.StrictlyNecessary);
                        }
                    });
                }

                SetSandhillsConsentCookie(newSettings, daysToSetCookie(newSettings.ConsentStatus));
                if (RequireOptInConsent) {
                    if (currentUser.IsAuthenticated && prvCkySettings?.TrackingUserID > 0) {
                        SetSandhillsConsentCookie(newSettings, daysToSetCookie(newSettings.ConsentStatus), prvCkySettings.TrackingUserID);
                    }
                    setConsentChanged(newSettings);
                    saveConsentToDB(newSettings);
                }
                return newSettings;
            });
        }
        setShowModal(false);
    }

    const saveConsentToDB = (consentSettings) => {
        if (RequireOptInConsent && currentUser.IsAuthenticated) {
            sandhillsRequest({
                url: routes.SaveCookieConsent,
                method: 'post',
                data: { consentSettings: consentSettings },
                errorHandling: {
                    message: 'Error Saving Cookie Preferences.',
                    showDetails: true,
                    useConsole: true,
                    useAlert: false
                }
            }).then((response) => {
                //if successful, update WebAgreementConsentID 
                if (response) {
                    setConsentSettings(prvCkySettings => {
                        if (response.WebAgreementConsentID && response.WebAgreementConsentID > 0) {
                            prvCkySettings.WebAgreementConsentID = response.WebAgreementConsentID
                        }

                        prvCkySettings.ShouldResync = response.ShouldResync; 
                        SetSandhillsConsentCookie(response, daysToSetCookie(prvCkySettings.ConsentStatus));

                        if (response.TrackingUserID && response.TrackingUserID > 0) {
                            SetSandhillsConsentCookie(response, daysToSetCookie(prvCkySettings.ConsentStatus), response.TrackingUserID);
                        }

                        return prvCkySettings;
                    });
                }
            });
        }
    }

    const onChange = (e, classification) => {
        if (!stringEqualsIgnoreCase(classification,)) {
            setConsentSettings(prvCkySettings => ({ ...prvCkySettings, [classification]: e }))
        }
    }

    //if for some reason user deletes cookie and reopens the modal,
    //we need to reset the ckySettings state
    const resetConsentSettings = () => {
        setConsentSettings(prvCkySettings => {
            const newSettings = {
                ...prvCkySettings,
                ConsentStatus: Actions.Deny,
                ActionDateTime: null,
                ShouldResync:RequireOptInConsent,
            };

            Classifications.forEach(c => {
                newSettings[c] = stringEqualsIgnoreCase(c, sandhillsCookieClassifications.StrictlyNecessary);
            });

            return newSettings;
        });
    }

    const getStatus = (Accept, currentStatus) => {
        var status = currentStatus;
        if (Accept) {
            if (currentStatus === Actions.Deny) {
                status = Actions.Accept;
            } else if (currentStatus === Actions.Accept || currentStatus === Actions.Update) {
                status = Actions.Update;
            } else {
                status = Actions.Deny;
            }
        }
        return status
    }

    const daysToSetCookie = (status) => {
        var days = 400;
        if (RequireOptInConsent && (status == Actions.Deny)) {
            days = 30;
        }
        return days;
    }

    const getLabel = (classification, label) => {

        if (stringEqualsIgnoreCase(classification, sandhillsCookieClassifications.StrictlyNecessary)) {
            return (<span dangerouslySetInnerHTML={{ __html: label }} />);
        }

        return label;
    }

    return (
        <>
            {(showModal && !useUserCentricsCMP) &&
                (<Modal containerClassName='cookies-consent' allowScroll={true}>
                    <Modal.Header onClose={() => onSubmit(sandhillsCookieClassifications.StrictlyNecessary, false)} xMarkClassName='fa-regular fa-circle-xmark' >
                        <div className='cookies-inner'>
                            <span dangerouslySetInnerHTML={{ __html: Message }}></span>
                        </div>
                    </Modal.Header>

                    {RequireOptInConsent &&
                        <>
                            <Modal.Body bodyClassName='toggle-container'>
                                {Toggles.map(t =>
                                    <Toggle
                                        classNames={'toggle-label' + (stringEqualsIgnoreCase(t.Classification, sandhillsCookieClassifications.StrictlyNecessary) ? ' shouldDisable' : '')}
                                        id={t.Id + '-toggle'}
                                        key={t.Id}
                                        IsChecked={ckySettings[t.Classification]}
                                        isHidden={stringEqualsIgnoreCase(t.Classification, sandhillsCookieClassifications.StrictlyNecessary)}
                                        onChange={(e) => onChange(e, t.Classification)}
                                        label={getLabel(t.Classification, t.Label)}
                                        isLabelWrapped
                                    />)}
                            </Modal.Body>
                            <div className="btn-container">
                                {Buttons.map((b) => <button className='btn' key={b.Id} id={b.Id} onClick={() => onSubmit(b.Classification, true)}>{b.Label}</button>)}
                            </div>
                        </>}
                </Modal>)
            }
        </>
    )
};

export default CookiesConsentView;
global.CookiesConsentView = CookiesConsentView;